import {
  calcDateFromDuration,
  isDuration,
  isEmptyDuration,
  MarketAccountStatusEnum,
  MarketAccountTypeEnum,
  toBig,
  type Duration,
  type MarketAccount,
} from '@talos/kyoko';

export interface CustomRateSideInput {
  price: string;
  size: string;
}

export interface CustomRateFieldErrors {
  marketAccount?: string;
  bidPrice?: string;
  bidSize?: string;
  offerPrice?: string;
  offerSize?: string;
  validUntil?: string;
}

export type CustomRateValidUntilInput = Date | Duration | null;

export function validateCustomRateForm({
  marketAccount,
  bid,
  offer,
  validUntil,
  relativeTo = new Date(),
}: {
  marketAccount?: MarketAccount;
  bid: CustomRateSideInput;
  offer: CustomRateSideInput;
  validUntil?: CustomRateValidUntilInput;
  relativeTo?: Date;
}): CustomRateFieldErrors {
  const errors = getCustomRateFieldErrors({ bid, offer });

  if (marketAccount == null) {
    errors.marketAccount = 'Account is required.';
  } else if (!isActiveInternalTradingMarketAccount(marketAccount)) {
    errors.marketAccount = 'Account must be an active trading account on the internal market.';
  }

  errors.validUntil = getCustomRateValidUntilError(validUntil, relativeTo);

  return errors;
}

export function getCustomRateValidUntilError(validUntil: CustomRateValidUntilInput | undefined, relativeTo: Date) {
  if (validUntil == null) {
    return undefined;
  }

  if (isDuration(validUntil)) {
    if (isEmptyDuration(validUntil)) {
      return undefined;
    }

    return undefined;
  }

  if (validUntil.getTime() <= relativeTo.getTime()) {
    return 'Valid until time must be in the future.';
  }

  return undefined;
}

export function resolveCustomRateValidUntil(validUntil: CustomRateValidUntilInput, relativeTo: Date) {
  if (validUntil == null) {
    return null;
  }

  if (isDuration(validUntil)) {
    if (isEmptyDuration(validUntil)) {
      return null;
    }

    return calcDateFromDuration(validUntil, relativeTo);
  }

  return validUntil;
}

export function getCustomRateFieldErrors({
  bid,
  offer,
}: {
  bid: CustomRateSideInput;
  offer: CustomRateSideInput;
}): CustomRateFieldErrors {
  const errors: CustomRateFieldErrors = {
    bidPrice: getPositiveNumberError(bid.price, 'Bid price'),
    bidSize: getPositiveNumberError(bid.size, 'Bid size'),
    offerPrice: getPositiveNumberError(offer.price, 'Offer price'),
    offerSize: getPositiveNumberError(offer.size, 'Offer size'),
  };

  if (errors.bidPrice == null && errors.offerPrice == null && !isBidLessThanOrEqualToOffer(bid.price, offer.price)) {
    errors.offerPrice = 'Offer price must be greater than or equal to bid price.';
  }

  return errors;
}

function getPositiveNumberError(value: string, label: string) {
  if (value === '') {
    return `${label} is required.`;
  }

  if (toBig(value)?.gt(0) !== true) {
    return `${label} must be greater than 0.`;
  }

  return undefined;
}

function isBidLessThanOrEqualToOffer(bidPrice: string, offerPrice: string) {
  return toBig(bidPrice)?.lte(offerPrice) === true;
}

export function hasCustomRateErrors(errors: CustomRateFieldErrors) {
  return Object.values(errors).some(error => error != null);
}

export function isActiveInternalTradingMarketAccount(marketAccount: MarketAccount) {
  return (
    marketAccount.Market === 'internal' &&
    marketAccount.Type === MarketAccountTypeEnum.Trading &&
    marketAccount.Status === MarketAccountStatusEnum.Active
  );
}
